import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import stylesPc from "./ContentSearch.module.css";
import stylesSp from "./ContentSearch_SP.module.css";
import {
    Button,
    createStyles,
    createTheme,
    Fab,
    FilledInput,
    InputAdornment,
    makeStyles,
    MenuItem,
    Modal,
    Select,
    Slide,
    Theme,
    ThemeProvider,
} from "@material-ui/core";
import PuiTypography from "../../../components/elements/PuiTypography";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { search } from "../../../../src/viewer/search/SearchActions";
import { Checkbox, FormControlLabel, CircularProgress } from "@material-ui/core";
import { Search, Clear, Check } from "@material-ui/icons";
import { CounterObject, PresenterList, ShowResult, Tag } from "../Search";
import ContentSearchDetail from "./ContentSearchDetail";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import FilterListIcon from "@material-ui/icons/FilterList";
import SelectLanguage from "../../../pages/vod/Common/SelectLanguage";
import { t } from "i18next";

const SELECT_ITEM_HEIGHT = 20;
const SELCECT_ITEM_PADDING_TOP = 8;

/**
|--------------------------------------------------
| サーチ画面
|--------------------------------------------------
*/

interface ContentSearchIF {
    changeOpenDrawerMenu?: Dispatch<SetStateAction<boolean>>;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        actionButton: {
            position: "fixed",
            bottom: "30px",
            right: "20px",
            zIndex: 200,
            backgroundColor: "var(--studio-btn-bg)",
            color: "var(--white)",
            "&:hover": {
                backgroundColor: "var(--studio-btn-bg)",
            },
        },
        formSelect: {
            border: "1px solid var(--gray-38)",
            width: IsDevice("iphone") ? "130px" : "224px",
            color: "var(--gray-80)",
            height: "40px",
            fontSize: "14px",
            background: "#FFFFFF0D",
            borderRadius: "5px",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "& .MuiSelect-icon": {
                color: "white",
            },
            "& .MuiSelect-selectMenu": {
                color: "white",
            },
        },
        dateItem: {
            display: "flex",
            backgroundColor: "var(--textfield-bg)",
            color: "white",
            fontSize: "14px",
            padding: "11px 16px",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "&:hover": {
                color: "white",
                backgroundColor: "#35393F",
            },
            ".Mui-selected": {
                backgroundColor: "#35393F",
            },
        },
        checkBox: {
            color: "var(--gray-80)",
        },
        dropdownStyle: {
            color: "white",
            backgroundColor: "var(--textfield-bg)",
            marginTop: "48px",
        },
        menuItem: {
            backgroundColor: "var(--textfield-bg)",
            color: "white",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "&:hover": {
                color: "white",
                backgroundColor: "#FFFFFF0D",
            },
        },
        searchDropdownStyle: {
            marginTop: "55px",
            width: "224px",
            color: "white",
            backgroundColor: "var(--textfield-bg)",
            height: "317px",
            "& .MuiList-padding": {
                paddingTop: "0",
                paddingBottom: "0",
            },
        },
    })
);

const theme = createTheme({
    palette: {
        primary: {
            main: "#16ADC1",
        },
        secondary: {
            main: "#4395EA",
        },
    },
});

export default function ContentSearch({ changeOpenDrawerMenu }: ContentSearchIF) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;
    const classes = useStyles();
    /* 変数の設定 */
    const [inputSearchWord, setInputSearchWord] = useState<string>("");
    const [isSlideOut, setIsSlideOut] = useState<boolean>(false);
    const [isOpenSearchModal, ShowSearchModal] = useState(false);
    const dispatch = useDispatch();
    const { result, isSearching } = useSelector((state: AppState) => ({
        result: state.search.result,
        isSearching: state.search.isSearching,
    }));

    //プレゼンターフィルタリング用
    const [presenterList, setPresenterList] = useState<PresenterList[]>([]);
    const [pIds, setPids] = useState<number[]>([]);

    const [isOpenSearchInput, changeOpenSearchInput] = useState<boolean>(false);

    //タグフィルタリング用
    const [tagList, setTagList] = useState<Tag[]>([]);
    const [tIds, setTids] = useState<number[]>([]);

    //キャプションの言語のフィルタリング(選択言語のラベル表示用)
    const [selectLanguage, setSelectLanguage] = useState<string>("ja");

    //検索結果のキャプション言語のみ表示
    const [showLanguage, setShowLanguage] = useState<string[]>([]);

    // 並び順filter
    const [filterDate, setFilterDate] = useState<string>(String(t("最新順")));

    // 並び順filter押下時
    const handleSortDate = (old: boolean, filter: string) => {
        // コンテンツ順番設定
        const sortedContentList = filteredResultList.sort((a, b) => {
            if (old) {
                return a.id - b.id;
            } else {
                return b.id - a.id;
            }
        });
        setFilteredResultList(sortedContentList);
        setFilterDate(filter);
    };

    //フィルタリングしたコンテンツリスト
    const [filteredResultList, setFilteredResultList] = useState<ShowResult[]>([]);

    //filter
    const [openFilter, setOpenFilter] = useState(false);

    //ユーザー情報
    const { userInfo } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
    }));

    const ResetSearch = () => {
        setPresenterList([]);
        setPids([]);
        setTagList([]);
        setTids([]);
        setFilteredResultList([]);
    };
    // Enterキーで検索
    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && inputSearchWord.length > 0) {
                if (userInfo && inputSearchWord) {
                    //コンテンツ詳細情報取得
                    dispatch(search(userInfo?.id, inputSearchWord.split(" ")));
                    ShowSearchModal(true);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inputSearchWord]
    );

    // 検索ボタンを押下
    const handleSearch = useCallback(() => {
        if (inputSearchWord.length > 0) {
            if (userInfo && inputSearchWord) {
                //コンテンツ詳細情報取得
                dispatch(search(userInfo?.id, inputSearchWord.split(" ")));
                ShowSearchModal(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputSearchWord]);

    // 検索モーダルを開く
    const OpenSearchModal = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputSearchWord(event.target.value);
    };

    // 検索モーダルを閉じる
    const CloseSearchModal = () => {
        ShowSearchModal(false);
        ResetSearch();
        if (changeOpenDrawerMenu) changeOpenDrawerMenu(false);
    };

    /* プレゼンターチェックをチェック処理 */
    const handlePresenterCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        //チャックされたプレゼンターID
        const chkPresenterId = Number(event.target.value);
        if (!pIds.includes(chkPresenterId)) {
            const newPids = [...pIds];
            newPids.push(chkPresenterId);
            setPids(newPids);
        } else {
            const newPids = pIds.filter((id) => id !== chkPresenterId);
            setPids(newPids);
        }
    };

    /* タグをチェック処理 */
    const handleTagCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        //チャックされたタグID
        const chkTagId = Number(event.target.value);
        if (!tIds.includes(chkTagId)) {
            const newTids = [...tIds];
            newTids.push(chkTagId);
            setTids(newTids);
        } else {
            const newTids = tIds.filter((id) => id !== chkTagId);
            setTids(newTids);
        }
    };

    //STT言語再配列処理
    useEffect(() => {
        const langlist: string[] = [];
        result.forEach((result) => {
            result.lang &&
                result.lang.forEach((lang) => {
                    langlist.push(lang);
                });
        });
        const showLanguage = langlist.filter(
            (value, index, array) => array.indexOf(value) === index
        );
        setShowLanguage(showLanguage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    //フィルタリング設定
    useEffect(() => {
        if (result) {
            setFilteredResultList(result);
        }
        /*  プレゼンターフィルタリング用 */
        const tempPresenterNames: PresenterList[] = [];
        let tempPresentContentCount: CounterObject = {};

        /* タグフィルタリング用*/
        const tempTagList: Tag[] = [];
        let tempTagContentCount: CounterObject = {};

        result.length !== 0 &&
            result.forEach((content) => {
                const isExistingPresenter = tempPresenterNames.find(
                    (presenter) => presenter.id === content.presenterId
                )
                    ? true
                    : false;

                //プレゼンター名を重複で表示しない様に判断
                if (!isExistingPresenter) {
                    //プレゼンター名とIDをリストに新しい追加
                    tempPresenterNames.push({
                        id: content.presenterId,
                        name: content.presenterName,
                    });
                    //プレゼンターに寄ってコンテンツ数を設定
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: 1,
                    };
                } else {
                    //プレゼンターはリストにある場合、コンテンツ数＋１
                    const contetnCountByPresenter = tempPresentContentCount[content.presenterId];
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: contetnCountByPresenter + 1,
                    };
                }

                content.tagList.forEach((tag: any) => {
                    const isExistingTag = tempTagList.find((tempTag) => tempTag.id === tag.id)
                        ? true
                        : false;

                    if (!isExistingTag) {
                        //フィルタリングタグリストにタグを追加
                        tempTagList.push({
                            id: tag.id,
                            title: tag.title,
                        });
                        //タグが付けたコンテンツ数の設定
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: 1,
                        };
                    } else {
                        //タグがリストにある場合、コンテンツ数+1
                        const contentCountByTag = tempTagContentCount[tag.id];
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: contentCountByTag + 1,
                        };
                    }
                });
            });

        //コンテンツ数降順でプレゼンターフィルタリング設定
        const sortedPresenterList = tempPresenterNames.sort((presenter1, presenter2) => {
            return tempPresentContentCount[presenter1.id] > tempPresentContentCount[presenter2.id]
                ? -1
                : 1;
        });

        // //コンテンツ数降順でタグリストフィルタリング設定
        const sortedTagList = tempTagList.sort((tag1, tag2) => {
            return tempTagContentCount[tag1.id] > tempTagContentCount[tag2.id] ? -1 : 1;
        });

        setPresenterList(sortedPresenterList);

        /*  タグフィルタリング*/
        setTagList(sortedTagList);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    //フィルタリング設定
    useEffect(() => {
        /*  プレゼンターフィルタリング用 */
        const tempPresenterNames: PresenterList[] = [];
        let tempPresentContentCount: CounterObject = {};

        /* タグフィルタリング用*/
        const tempTagList: Tag[] = [];
        let tempTagContentCount: CounterObject = {};

        filteredResultList.length !== 0 &&
            filteredResultList.forEach((content) => {
                const isExistingPresenter = tempPresenterNames.find(
                    (presenter) => presenter.id === content.presenterId
                )
                    ? true
                    : false;

                //プレゼンター名を重複で表示しない様に判断
                if (!isExistingPresenter) {
                    //プレゼンター名とIDをリストに新しい追加
                    tempPresenterNames.push({
                        id: content.presenterId,
                        name: content.presenterName,
                    });
                    //プレゼンターに寄ってコンテンツ数を設定
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: 1,
                    };
                } else {
                    //プレゼンターはリストにある場合、コンテンツ数＋１
                    const contetnCountByPresenter = tempPresentContentCount[content.presenterId];
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: contetnCountByPresenter + 1,
                    };
                }

                content.tagList.forEach((tag: any) => {
                    const isExistingTag = tempTagList.find((tempTag) => tempTag.id === tag.id)
                        ? true
                        : false;

                    if (!isExistingTag) {
                        //フィルタリングタグリストにタグを追加
                        tempTagList.push({
                            id: tag.id,
                            title: tag.title,
                        });
                        //タグが付けたコンテンツ数の設定
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: 1,
                        };
                    } else {
                        //タグがリストにある場合、コンテンツ数+1
                        const contentCountByTag = tempTagContentCount[tag.id];
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: contentCountByTag + 1,
                        };
                    }
                });
            });

        //コンテンツ数降順でプレゼンターフィルタリング設定
        const sortedPresenterList = tempPresenterNames.sort((presenter1, presenter2) => {
            return tempPresentContentCount[presenter1.id] > tempPresentContentCount[presenter2.id]
                ? -1
                : 1;
        });

        // //コンテンツ数降順でタグリストフィルタリング設定
        const sortedTagList = tempTagList.sort((tag1, tag2) => {
            return tempTagContentCount[tag1.id] > tempTagContentCount[tag2.id] ? -1 : 1;
        });

        setPresenterList(sortedPresenterList);

        /*  タグフィルタリング*/
        setTagList(sortedTagList);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredResultList]);

    //表示データをフィルタリングする
    useEffect(() => {
        let tempFilterdResultList: ShowResult[] = [];
        if (pIds !== undefined && tIds !== undefined && pIds.length === 0 && tIds.length === 0) {
            tempFilterdResultList = [...result];
        } else {
            if (result) {
                if (pIds !== undefined && pIds.length !== 0) {
                    if (tIds !== undefined && tIds.length !== 0) {
                        pIds.forEach((pId) => {
                            filteredResultList.forEach((tempResult) => {
                                if (
                                    tempResult.presenterId === pId &&
                                    !tempFilterdResultList.includes(tempResult)
                                ) {
                                    tempFilterdResultList.push(tempResult);
                                }
                            });
                        });
                    } else {
                        pIds.forEach((pId) => {
                            result.forEach((tempResult) => {
                                if (
                                    tempResult.presenterId === pId &&
                                    !tempFilterdResultList.includes(tempResult)
                                ) {
                                    tempFilterdResultList.push(tempResult);
                                }
                            });
                        });
                    }
                } else {
                    if (tIds !== undefined && tIds.length !== 0) {
                        tIds.forEach((tId) => {
                            result.forEach((tempResult) => {
                                tempResult.tagList.forEach((tempTag) => {
                                    if (
                                        tempTag.id === tId &&
                                        !tempFilterdResultList.includes(tempResult)
                                    ) {
                                        tempFilterdResultList.push(tempResult);
                                    }
                                });
                            });
                        });
                    }
                }
            }
        }

        setFilteredResultList(tempFilterdResultList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pIds]);

    useEffect(() => {
        let tempFilterdResultList: ShowResult[] = [];
        if (pIds !== undefined && tIds !== undefined && pIds.length === 0 && tIds.length === 0) {
            tempFilterdResultList = [...result];
        } else {
            if (result) {
                if (tIds !== undefined && tIds.length !== 0) {
                    if (pIds !== undefined && pIds.length !== 0) {
                        tIds.forEach((tId) => {
                            filteredResultList.forEach((tempResult) => {
                                tempResult.tagList.forEach((tempTag) => {
                                    if (
                                        tempTag.id === tId &&
                                        !tempFilterdResultList.includes(tempResult)
                                    ) {
                                        tempFilterdResultList.push(tempResult);
                                    }
                                });
                            });
                        });
                    } else {
                        tIds.forEach((tId) => {
                            result.forEach((tempResult) => {
                                tempResult.tagList.forEach((tempTag) => {
                                    if (
                                        tempTag.id === tId &&
                                        !tempFilterdResultList.includes(tempResult)
                                    ) {
                                        tempFilterdResultList.push(tempResult);
                                    }
                                });
                            });
                        });
                    }
                } else {
                    if (pIds !== undefined && pIds.length !== 0) {
                        pIds.forEach((pId) => {
                            result.forEach((tempResult) => {
                                if (
                                    tempResult.presenterId === pId &&
                                    !tempFilterdResultList.includes(tempResult)
                                ) {
                                    tempFilterdResultList.push(tempResult);
                                }
                            });
                        });
                    }
                }
            }
        }

        setFilteredResultList(tempFilterdResultList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tIds]);

    const searchFilter = (
        <ThemeProvider theme={theme}>
            <div className={styles.left}>
                <div className={styles.filterHeader}>
                    <div className={styles.filterHeaderLable}>
                        <PuiTypography size={IsDevice("iphone") ? 14 : 16} bold={true}>
                            {t("絞り込み")}
                        </PuiTypography>
                    </div>
                </div>
                {/* Presenterタイトルエリア */}
                <div className={styles.filterHeader}>
                    <div className={styles.filterHeaderLable}>
                        <PuiTypography
                            size={IsDevice("iphone") ? 12 : 14}
                            color={"secondary-font"}
                            bold={true}
                        >
                            {t("プレゼンター")}
                        </PuiTypography>
                    </div>
                </div>
                {/* Presenterリストエリア */}
                <div className={styles.list}>
                    <Select
                        required
                        color="secondary"
                        id="presenter-select"
                        variant="outlined"
                        value={presenterList.map((pro) => pro.name)}
                        renderValue={(selected: any) =>
                            selected.length === 0 ? <p>{t("選択する")}</p> : selected.join(", ")
                        }
                        onChange={() => handlePresenterCheck}
                        defaultValue="-1"
                        MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            classes: {
                                paper: classes.searchDropdownStyle,
                            },
                        }}
                        className={classes.formSelect}
                    >
                        <MenuItem disabled value="-1">
                            <p>{t("選択する")}</p>
                        </MenuItem>
                        <div className={styles.checkList}>
                            {presenterList?.length !== 0 &&
                                presenterList.map((presenter) => (
                                    <div className={styles.tagItem}>
                                        <FormControlLabel
                                            key={presenter.id}
                                            name={`presenter${presenter.id}`}
                                            className={classes.menuItem}
                                            control={
                                                <Checkbox
                                                    id={String(presenter.id)}
                                                    value={presenter.id}
                                                    name={`presenterChk${presenter.id}`}
                                                    onChange={handlePresenterCheck}
                                                    checked={
                                                        pIds.includes(presenter.id) ? true : false
                                                    }
                                                    color="primary"
                                                    className={classes.checkBox}
                                                />
                                            }
                                            label={
                                                <>
                                                    <div className={styles.checkboxLable}>
                                                        <div
                                                            className={styles.filterCheckboxLable}
                                                            data-for={presenter.name}
                                                        >
                                                            <PuiTypography
                                                                size={14}
                                                                color={"primary-font"}
                                                            >
                                                                {presenter.name}
                                                            </PuiTypography>
                                                        </div>
                                                    </div>
                                                </>
                                            }
                                        />
                                    </div>
                                ))}
                        </div>
                    </Select>
                </div>
                {/* タグタイトルエリア */}
                <div className={styles.filterHeader}>
                    <div className={styles.filterHeaderLable}>
                        <PuiTypography
                            size={IsDevice("iphone") ? 12 : 14}
                            color={"secondary-font"}
                            bold={true}
                        >
                            {t("タグ")}
                        </PuiTypography>
                    </div>
                </div>
                {/* タグリストエリア */}
                <div className={styles.list}>
                    <Select
                        color="secondary"
                        id="tag-select"
                        variant="outlined"
                        value={tagList.map((tag) => tag.title)}
                        multiple
                        displayEmpty
                        renderValue={(selected: any) =>
                            selected.length === 0 ? <p>{t("選択する")}</p> : selected.join(", ")
                        }
                        MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            classes: {
                                paper: classes.searchDropdownStyle,
                            },
                        }}
                        className={classes.formSelect}
                        style={{
                            maxHeight: SELECT_ITEM_HEIGHT * 4.5 + SELCECT_ITEM_PADDING_TOP,
                        }}
                    >
                        <MenuItem disabled value="">
                            <p>{t("選択する")}</p>
                        </MenuItem>
                        <div className={styles.checkList}>
                            {tagList &&
                                tagList?.length !== 0 &&
                                tagList.map((tag) => (
                                    <div className={styles.tagItem}>
                                        <FormControlLabel
                                            key={tag.id}
                                            name={`tag${tag.id}`}
                                            className={classes.menuItem}
                                            control={
                                                <Checkbox
                                                    id={String(tag.id)}
                                                    value={tag.id}
                                                    name={`presenterChk${tag.id}`}
                                                    onChange={handleTagCheck}
                                                    checked={tIds.includes(tag.id) ? true : false}
                                                    color="primary"
                                                    className={classes.checkBox}
                                                />
                                            }
                                            label={
                                                <>
                                                    <div className={styles.checkboxLable}>
                                                        <div
                                                            className={styles.filterCheckboxLable}
                                                            data-for={tag.title}
                                                        >
                                                            <PuiTypography
                                                                size={14}
                                                                color={"primary-font"}
                                                            >
                                                                {tag.title}
                                                            </PuiTypography>
                                                        </div>
                                                    </div>
                                                </>
                                            }
                                        />
                                    </div>
                                ))}
                        </div>
                    </Select>
                </div>
                {/*  翻訳言語タイトルエリア */}
                <div className={styles.filterHeader}>
                    <div className={styles.filterHeaderLable}>
                        <PuiTypography
                            size={IsDevice("iphone") ? 12 : 14}
                            color={"secondary-font"}
                            bold={true}
                        >
                            {t("翻訳言語")}
                        </PuiTypography>
                    </div>
                </div>
                {/* 翻訳言語エリア */}
                <div className={styles.list}>
                    <div className={styles.selectLanguage}>
                        <SelectLanguage
                            selectLanguage={selectLanguage}
                            setInputLanguage={setSelectLanguage}
                            isSearch={true}
                            searchLanguage={showLanguage}
                        />
                    </div>
                </div>
            </div>
        </ThemeProvider>
    );

    return (
        <>
            {/* サーチ入力エリア */}

            {IsDevice("desktop") ? (
                isOpenSearchInput ? (
                    <FilledInput
                        placeholder={String(t("検索する"))}
                        id="searchInput"
                        value={inputSearchWord}
                        onChange={OpenSearchModal}
                        onKeyPress={handleKeyPress}
                        className={`${styles.searchInput} ${isSlideOut ? styles.slideOut : ""}`}
                        disableUnderline={true}
                        autoComplete={"off"}
                        autoFocus={true}
                        endAdornment={
                            <InputAdornment position="start">
                                <Button
                                    startIcon={
                                        <Search
                                            style={{
                                                color: "var(--white)",
                                            }}
                                        />
                                    }
                                    className={styles.searchButton}
                                    onClick={handleSearch}
                                ></Button>
                                <Button
                                    endIcon={
                                        <Clear
                                            style={{
                                                color: "var(--loading-bg)",
                                            }}
                                        />
                                    }
                                    className={styles.clearButton}
                                    onClick={() => {
                                        setIsSlideOut(true);
                                        setTimeout(() => {
                                            setIsSlideOut(false);
                                            changeOpenSearchInput(false);
                                            setInputSearchWord("");
                                        }, 400);
                                    }}
                                />
                            </InputAdornment>
                        }
                    ></FilledInput>
                ) : (
                    <InputAdornment
                        position="end"
                        onClick={() => changeOpenSearchInput(true)}
                        className={styles.searchOpenButton}
                    >
                        <Button
                            endIcon={
                                <Search
                                    style={{
                                        color: "var(--loading-bg)",
                                    }}
                                />
                            }
                        ></Button>
                    </InputAdornment>
                )
            ) : (
                <FilledInput
                    placeholder={String(t("検索する"))}
                    id="searchInput"
                    value={inputSearchWord}
                    onChange={OpenSearchModal}
                    onKeyPress={handleKeyPress}
                    className={styles.drawerSearchInput}
                    style={{ left: "-28px" }}
                    disableUnderline={true}
                    autoComplete={"off"}
                    endAdornment={
                        <InputAdornment position="start">
                            <Button
                                startIcon={
                                    <Search
                                        style={{
                                            fontFamily: "Yu Gothic",
                                            color: "var(--white)",
                                            marginTop: "-4px",
                                        }}
                                    />
                                }
                                className={styles.searchButton}
                                onClick={handleSearch}
                            ></Button>
                            <Button
                                endIcon={
                                    <Clear
                                        style={{
                                            color: "var(--loading-bg)",
                                            marginTop: "14px",
                                        }}
                                    />
                                }
                                className={styles.clearButton}
                                onClick={() => {
                                    changeOpenSearchInput(false);
                                    setInputSearchWord("");
                                }}
                            />
                        </InputAdornment>
                    }
                ></FilledInput>
            )}
            {/* サーチ画面モーダル */}
            <Modal
                className={styles.searchResultModal}
                aria-labelledby="spring-modal-title"
                aria-describedby="spring-modal-description"
                aria-hidden={false}
                open={isOpenSearchModal}
                onClose={CloseSearchModal}
                closeAfterTransition
            >
                <div className={styles.resultContainer}>
                    {/* 検索中にぐるぐるする */}
                    {isSearching && (
                        <div className={styles.noContent}>
                            <CircularProgress size={60} style={{ color: "var(--loading-bg)" }} />
                        </div>
                    )}
                    {/* 検索結果を表示する*/}
                    {!isSearching && (
                        <>
                            {/* 検索結果が０件の場合、 */}
                            {result && result.length === 0 && (
                                <>
                                    <div className={styles.noResult}>
                                        <PuiTypography size={17} bold={true}>
                                            「{inputSearchWord} 」
                                            {t("と該当する検索結果はありません ")}
                                        </PuiTypography>
                                    </div>
                                </>
                            )}
                            {/* 結果結果が１件以上の場合、 */}
                            {result && result.length !== 0 && (
                                <>
                                    <ThemeProvider theme={theme}>
                                        {/* フィルタリングエリア */}
                                        {IsDevice("desktop") && searchFilter}
                                        <div className={styles.right}>
                                            {/*結果件数表示エリア */}
                                            <div className={styles.resultCountDiv}>
                                                <PuiTypography size={17} bold={true}>
                                                    「{inputSearchWord} 」{t("の検索結果")}（
                                                    {filteredResultList &&
                                                    filteredResultList.length !== 0
                                                        ? filteredResultList.length
                                                        : 0}
                                                    ）
                                                </PuiTypography>
                                                {IsDevice("desktop") && (
                                                    <Select
                                                        color="secondary"
                                                        id="date-order-select"
                                                        variant="outlined"
                                                        value={filterDate}
                                                        defaultValue={t("新着順")}
                                                        className={classes.formSelect}
                                                        renderValue={() =>
                                                            filterDate === t("最新順")
                                                                ? t("最新順")
                                                                : t("古い順")
                                                        }
                                                        MenuProps={{
                                                            getContentAnchorEl: null,
                                                            anchorOrigin: {
                                                                vertical: "top",
                                                                horizontal: "left",
                                                            },
                                                            transformOrigin: {
                                                                vertical: "top",
                                                                horizontal: "left",
                                                            },
                                                            classes: {
                                                                paper: classes.dropdownStyle,
                                                            },
                                                        }}
                                                        style={{
                                                            width: "160px",
                                                            display: "flex",
                                                            position: "relative",
                                                            left: "15%",
                                                            marginTop: "7px",
                                                        }}
                                                    >
                                                        <MenuItem
                                                            value={t("最新順")}
                                                            className={classes.dateItem}
                                                            onClick={() =>
                                                                handleSortDate(false, "最新順")
                                                            }
                                                        >
                                                            {t("最新順")}
                                                            {filterDate === "最新順" && (
                                                                <Check
                                                                    className={styles.checkIcon}
                                                                />
                                                            )}
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={t("古い順")}
                                                            className={classes.dateItem}
                                                            onClick={() =>
                                                                handleSortDate(true, "古い順")
                                                            }
                                                        >
                                                            {t("古い順")}
                                                            {filterDate === "古い順" && (
                                                                <Check
                                                                    className={styles.checkIcon}
                                                                />
                                                            )}
                                                        </MenuItem>
                                                    </Select>
                                                )}
                                            </div>
                                            {filteredResultList && (
                                                <>
                                                    {/* 検索結果表示*/}
                                                    {filteredResultList.length !== 0 &&
                                                        filteredResultList.map((content) => (
                                                            <ContentSearchDetail
                                                                content={content}
                                                                key={content.id}
                                                                ShowSearchModal={ShowSearchModal}
                                                                changeOpenDrawerMenu={
                                                                    changeOpenDrawerMenu
                                                                }
                                                                selectLanguage={selectLanguage}
                                                            />
                                                        ))}
                                                </>
                                            )}
                                        </div>
                                    </ThemeProvider>
                                </>
                            )}
                        </>
                    )}

                    {!IsDevice("desktop") && (
                        <>
                            <Fab
                                variant="circular"
                                size="small"
                                onClick={() => setOpenFilter(!openFilter)}
                                className={classes.actionButton}
                            >
                                {openFilter ? (
                                    <img
                                        src={
                                            require("../../../assets/image/closeFilter.svg").default
                                        }
                                        alt="tag"
                                    />
                                ) : (
                                    <FilterListIcon />
                                )}
                            </Fab>
                            <Slide direction="left" in={openFilter}>
                                <div className={styles.searchFilter}>{searchFilter}</div>
                            </Slide>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
}
